
import { Box,  Typography,  MenuItem } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import CustomDropdown from 'components/Dropdown';
import { useEffect } from 'react';

type Props = {
  value: string;
  onChange: (next: string) => void;
  groupByList: GroupByListItem[];
  includeGroupByMenuColumns?: string[]; 
};

type GroupByListItem = {
  value: string;  
  label: string;  
};

const menuItemTypography = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 400,
  fontSize: '14px',
  lineHeight: '18px',
  letterSpacing: '0px',
  textTransform: 'none',
};

const selectedItemTypography = {
  fontFamily: 'Montserrat, sans-serif',
  fontWeight: 600,
  fontSize: '14px',
  lineHeight: '18px',
  letterSpacing: '0px',
  textTransform: 'none',
};

export const GroupByDropdown: React.FC<Props> = ({ 
  value, 
  onChange, 
  groupByList,
  includeGroupByMenuColumns = [] 
}) => {
 
  const filteredGroupByList = (includeGroupByMenuColumns.length > 0
    ? groupByList.filter((opt) => includeGroupByMenuColumns.includes(opt.value))
    : groupByList
  ).filter((opt) => opt.value !== 'none');

  useEffect(() => {
    if (value !== 'none') {
      const isValid = filteredGroupByList.some(opt => opt.value === value);
      if (!isValid) {
        console.warn(`Group by value "${value}" is not in included columns list, resetting to "none"`);
        onChange('none');
      }
    }
  }, [value, filteredGroupByList, onChange]);

  const isGroupBySelected = value !== 'none';

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        borderRadius: '2px',
        border: '1px solid #E4E4E7',
        backgroundColor: '#FFFFFF',
        height: 40,
        px: 1,
        minWidth: 280,
        maxWidth: 300,
      }}
    >
      <Typography
        sx={{
          fontSize: 14,
          color: '#6B7280',
          mr: 0,
          flexShrink: 0,
        }}
      >
        Group By :
      </Typography>
      <CustomDropdown
        dropdownIcon={ExpandMoreIcon}
        value={value}
        handleChange={(e: any) => onChange(e.target.value)}
        boxProps={{
          sx: {
            backgroundColor: '#FFFFFF',
            width: 200,
            height: 40,
            color: '#FfFFFF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            ml: 3,
            flexShrink: 0,
          },
        }}
        selectComponentProps={{
          sx: {
            width: '100%',
            height: 40,
            display: 'flex',
            alignItems: 'center',

            '& .MuiSelect-select': {
              padding: 0,
              pr: '24px',
              fontSize: 14,
              fontWeight: 600,
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              ml: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            },
            '& fieldset, & .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiSelect-icon': {
              color: '#2563EB',
            },
          },
          renderValue: (selected: any) => {
            if (selected === 'none') return 'NO GROUPING';
            const selectedOption = filteredGroupByList.find(opt => opt.value === selected);
            return selectedOption?.label?.toUpperCase() || 'Group By';
          },
        }}
        menuProps={{
          PaperProps: {
            sx: {
              mt: 0.5,
              borderRadius: '4px',
              boxShadow: '0px 12px 30px rgba(15, 23, 42, 0.18)',
              backgroundColor: '#FBFDFF',
              minWidth: 280,
              maxWidth: 280,
            },
          },
          MenuListProps: {
            sx: {
              py: 0.5,
              backgroundColor: '#FBFDFF',
              color: '#FBFDFF',
            },
          },
        }}
      >
        {/* "Group By" header - bold when selected (value !== 'none') */}
        <MenuItem
          sx={{
            ...(isGroupBySelected ? selectedItemTypography : menuItemTypography),
            color: '#000000',
            cursor: 'default',
            fontSize: 14,
            '&.Mui-disabled': {
              opacity: 1,
            },
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          Group By
        </MenuItem>

        <MenuItem
          value="none"
          sx={{
            ...(value === 'none' ? selectedItemTypography : menuItemTypography),
            color: '#000000',
            fontStyle: 'italic',

            '&:hover': {
              backgroundColor: '#E5F2FF',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              gap: 2,
              fontSize: 14,
            }}
          >
            <span style={{ fontSize: 14 }}>NO GROUPING</span>
            {value === 'none' && (
              <CheckIcon
                sx={{
                  fontSize: 20,
                  color: '#003771',
                  flexShrink: 0,
                }}
              />
            )}
          </Box>
        </MenuItem>



        {filteredGroupByList.map((opt) => {
          const isSelected = value === opt.value;
          return (
            <MenuItem
              key={opt.value}
              value={opt.value}
              sx={{
                ...menuItemTypography,
                color: '#111827',
                fontSize: 14,
                backgroundColor: isSelected ? '#E5F2FF' : 'transparent',
                '&:hover': {
                  backgroundColor: '#E5F2FF',
                },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  width: '100%',
                  gap: 2,
                }}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    flex: 1,
                    fontSize: 14,
                  }}>
                  {opt.label?.toUpperCase()}
                </span>
                {isSelected && (
                  <CheckIcon
                    sx={{
                      fontSize: 20,
                      color: '#003771',
                      flexShrink: 0,
                    }}
                  />
                )}
              </Box>
            </MenuItem>
          );
        })}
      </CustomDropdown>
    </Box>
  );
};